import { Injectable, inject } from '@angular/core'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService, LoaderActionTypeEnum } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Appearance, Stripe, StripeElements, StripePaymentElementOptions } from '@stripe/stripe-js'
import { StripeAccountTypeEnum } from 'libs/gb-dashboard/shared/data-access-stripe-connect/src/lib/+state/stripe-connect.models'
import { STRIPE_LOADER } from 'gb-dashboard/shared/data-access-stripe'
import { catchError, exhaustMap, finalize, from, map, mergeMap, of, switchMap, tap, withLatestFrom, zip } from 'rxjs'
import { GbStripeService } from '../gb-stripe.service'
import { StripeApiService } from './stripe-api.service'
import * as StripeActions from './stripe.actions'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { Action } from '@ngrx/store'
import * as PaymentsActions from 'libs/gb-dashboard/checkout/data-access-payments/src/lib/+state/payments.actions'
import { HttpErrorResponse } from '@angular/common/http'

@Injectable()
export class StripeEffects {
  private actions$ = inject(Actions)

  initStripeElements$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StripeActions.initStripeElements),
        withLatestFrom(this.selectedAuctionFacade.stripeUserId$, this.selectedAuctionFacade.isStripeExpress$),
        tap(([, stripeUserId, isExpress]) => {
          this.gbStripeService.initStripe(stripeUserId, isExpress).then(() => {
            const clientSecret = this.gbStripeService.paymentIntent?.client_secret

            if (!clientSecret) return console.error('no client secret found')

            const elements = this.gbStripeService.stripe?.elements({
              clientSecret,
              appearance: this.getElementsAppearance(),
              loader: 'never',
            })
            this.gbStripeService.stripeElements = elements

            if (!elements) return console.error('no stripe elements found')

            this.createPaymentElement(elements)
          })
        }),
      ),
    { dispatch: false },
  )

  setupNewCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.prepareForNewCardSetup),
      withLatestFrom(this.selectedAuctionFacade.auction$),
      tap(() => this.gbLoaderService.showLoaderDialog(STRIPE_LOADER.prepareCardForSetup.id)),
      switchMap(([action, auction]) => {
        if (!auction?.url_name) {
          throw new Error('No auction URL name')
        }
        // Get the client secret from setup intent
        return this.stripeApiService.setupIntent(action.profileUuid, auction.url_name).pipe(
          switchMap((setupIntent) => {
            this.gbStripeService.setupIntent = setupIntent
            const clientSecret = setupIntent.client_secret

            if (!clientSecret) {
              throw new Error('No client secret')
            }

            const isExpress = auction.stripe_connect?.account_type === StripeAccountTypeEnum.Express

            if (!auction.stripe_user_id) {
              throw new Error('No Stripe user ID')
            }

            return from(this.gbStripeService.initStripe(auction.stripe_user_id, isExpress)).pipe(
              map((stripe: Stripe | null) => {
                if (!stripe) {
                  throw new Error('Failed to initialize Stripe')
                }

                this.gbStripeService.stripe = stripe
                const elements = stripe.elements({
                  clientSecret,
                  appearance: this.getElementsAppearance(),
                  loader: 'auto',
                })

                if (!elements) {
                  throw new Error('No Stripe elements found')
                }

                this.gbStripeService.stripeElements = elements
                this.createPaymentElement(elements)
                return StripeActions.prepareForNewCardSetupSuccess()
              }),
            )
          }),
          catchError((error) => {
            console.error('Error in setupNewCard$:', error)
            return of(StripeActions.prepareForNewCardSetupFailure({ error: error.message || 'GENERIC.ERROR' }))
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(STRIPE_LOADER.prepareCardForSetup.id)),
        )
      }),
    ),
  )

  payWithAnotherCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.payWithAnotherCard),
      withLatestFrom(this.selectedAuctionFacade.auction$),
      tap(() => this.gbLoaderService.showLoaderDialog(STRIPE_LOADER.prepareCardForSetup.id)),
      switchMap(([action, auction]) => {
        if (!auction?.url_name) {
          throw new Error('No auction URL name')
        }
        // Get the client secret from setup intent
        // return this.stripeApiService.setupIntent(action.profileUuid, auction.url_name).pipe(
        //   switchMap((setupIntent) => {

        const clientSecret = action.clientSecret

        if (!clientSecret) {
          throw new Error('No client secret')
        }

        const isExpress = auction.stripe_connect?.account_type === StripeAccountTypeEnum.Express

        if (!auction.stripe_user_id) {
          throw new Error('No Stripe user ID')
        }

        return from(this.gbStripeService.initStripe(auction.stripe_user_id, isExpress)).pipe(
          map((stripe: Stripe | null) => {
            if (!stripe) {
              throw new Error('Failed to initialize Stripe')
            }

            this.gbStripeService.stripe = stripe
            const elements = stripe.elements({
              clientSecret,
              appearance: this.getElementsAppearance(),
              loader: 'auto',
            })

            if (!elements) {
              throw new Error('No Stripe elements found')
            }

            this.gbStripeService.stripeElements = elements
            this.createPaymentElement(elements)
            return StripeActions.payWithAnotherCardSuccess()
          }),
          catchError((error) => {
            console.error('Error in setupNewCard$:', error)
            return of(StripeActions.prepareForNewCardSetupFailure({ error: error.message || 'GENERIC.ERROR' }))
          }),
          finalize(() => this.gbLoaderService.hideLoaderDialog(STRIPE_LOADER.prepareCardForSetup.id)),
        )
      }),
    ),
  )

  addNewCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.addNewCard),
      withLatestFrom(this.selectedAuctionFacade.auction$),
      tap(() => this.gbLoaderService.showLoaderDialog(STRIPE_LOADER.addCard.id)),
      exhaustMap(([action, auction]) => zip([of(action), of(auction), this.gbStripeService.confirmCardSetup()])),
      switchMap(([action, auction, result]) => {
        if (result && result.setupIntent && result.setupIntent.payment_method) {
          return this.stripeApiService
            .attachPaymentMethod(
              result.setupIntent.payment_method.toString(),
              auction?.stripe_connect ? auction.stripe_connect.uuid : '',
              action.user,
              auction?.url_name || '',
              action.stripeCustomer,
            )
            .pipe(
              map(() => {
                if (action.onSuccess) {
                  action.onSuccess()
                }
                return StripeActions.addNewCardSuccess()
              }),
              catchError((error) => of(StripeActions.addNewCardFailure({ error }))),
            )
        }
        return of(StripeActions.addNewCardFailure({ error: result.error }))
      }),
    ),
  )

  addNewCardSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StripeActions.addNewCardSuccess),
        tap(() => {
          this.gbNotificationService.success('ADD_PAYMENT.CARD_ADDED_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  hideAddCardLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StripeActions.addNewCardFailure, StripeActions.addNewCardSuccess),
        tap(() => {
          this.gbLoaderService.hideLoaderDialog(STRIPE_LOADER.addCard.id)
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StripeActions.addNewCardFailure, StripeActions.prepareForNewCardSetupFailure),
        tap((action) => {
          let error: string | undefined = 'GENERIC.ERROR'
          if (typeof action.error == 'string') error = action.error
          else if (action.error instanceof HttpErrorResponse) {
            if (typeof action.error.error === 'string') error = action.error.error
            else error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          } else if (typeof action.error == 'object') {
            error = action.error.message || 'GENERIC.ERROR'
          }

          if (error) this.gbNotificationService.error(error)
        }),
      ),
    { dispatch: false },
  )

  handlePaymentCompletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.handlePaymentCompletion),
      map(() => {
        return StripeActions.onPaymentSuccess()
      }),
    ),
  )

  onPaymentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StripeActions.onPaymentSuccess),
      tap(() => {
        this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.ConfirmCardPayment)
      }),
      switchMap(() => {
        const actions: Action[] = []

        actions.push(PaymentsActions.showPaymentDialogSuccess())
        return actions
      }),
    )
  })

  cancelPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StripeActions.cancelPayment),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      mergeMap(([, urlName]) => {
        return this.stripeApiService.cancelPayment(urlName, this.gbStripeService?.payment?.uuid).pipe(
          map(() => StripeActions.cancelPaymentSuccess()),
          catchError((error: HttpErrorResponse) => of(StripeActions.cancelPaymentFailure({ error }))),
        )
      }),
    )
  })

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private stripeApiService: StripeApiService,
    private gbStripeService: GbStripeService,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbLoaderService: GbLoaderService,
  ) {}

  private createPaymentElement(elements: StripeElements): void {
    const options: StripePaymentElementOptions = {
      terms: { card: 'never' },
      // exclude Apple and Google pay as already shown in digital wallet
      wallets: { applePay: 'never', googlePay: 'never' },
      layout: 'tabs',
    }
    const paymentElement = elements.create('payment', options)
    this.gbStripeService.paymentElementSubject.next(paymentElement)
    paymentElement.on('ready', () => this.gbStripeService.paymentElementReadySubject.next(true))
  }

  private getElementsAppearance(): Appearance {
    return {
      variables: {
        fontSizeBase: '15px',
        borderRadius: '30px',
      },
      rules: {
        '.Input': {
          borderRadius: '5px',
        },
        '.Tab': {
          borderRadius: '5px',
        },
        '.Label': {
          fontSize: '0',
        },
      },
    }
  }
}
